<script setup lang="ts">
import { models } from 'cesg-shared';
import { ProvisioningStatus } from 'cesg-shared/dist/models';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { isStaleInProgress } from './project-sharepoint-status';

const { t } = useI18n();

const props = defineProps<{
  sharePoint: models.ProjectWorkspaceStatus | null;
}>();

defineEmits<{ provision: [] }>();

const isStale = computed(() => isStaleInProgress(props.sharePoint));

/** A stale IN_PROGRESS row reads as failed everywhere in this cell: nothing moves it on its own
 * (no cron — D2), so presenting it as "provisioning" would be a lie the user can't act on. */
const effectiveStatus = computed<ProvisioningStatus | null>(() => {
  if (!props.sharePoint) return null;
  return isStale.value ? ProvisioningStatus.FAILED : props.sharePoint.status;
});

const chip = computed(() => {
  switch (effectiveStatus.value) {
    case ProvisioningStatus.READY:
      return { label: t('adminProjects.sharePointReady'), colour: 'is-success' };
    case ProvisioningStatus.FAILED:
      return { label: t('adminProjects.sharePointFailed'), colour: 'is-danger' };
    case ProvisioningStatus.PENDING:
    case ProvisioningStatus.IN_PROGRESS:
      return { label: t('adminProjects.sharePointProvisioning'), colour: 'is-info' };
    default:
      return { label: t('adminProjects.sharePointNotProvisioned'), colour: 'is-light' };
  }
});

const showProvision = computed(() => effectiveStatus.value === null);
const showRetry = computed(() => effectiveStatus.value === ProvisioningStatus.FAILED);
const siteUrl = computed(() =>
  effectiveStatus.value === ProvisioningStatus.READY ? props.sharePoint?.siteUrl : null,
);
</script>

<template>
  <span class="tag" :class="chip.colour">{{ chip.label }}</span>

  <a
    v-if="siteUrl"
    class="sharepoint-open"
    :href="siteUrl"
    target="_blank"
    rel="noopener"
    @click.stop
  >
    {{ t('adminProjects.sharePointOpenSite') }}
  </a>
  <button
    v-else-if="showProvision"
    class="button is-small sharepoint-provision"
    @click.stop="$emit('provision')"
  >
    {{ t('adminProjects.sharePointProvision') }}
  </button>
  <button
    v-else-if="showRetry"
    class="button is-small sharepoint-retry"
    @click.stop="$emit('provision')"
  >
    {{ t('adminProjects.sharePointRetry') }}
  </button>

  <span
    v-if="sharePoint?.status === ProvisioningStatus.FAILED && sharePoint.lastError"
    class="has-text-danger is-size-7 sharepoint-error"
  >
    {{ sharePoint.lastError }}
  </span>
</template>
